import { Disclosure, Menu, Transition } from "@headlessui/react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Fragment, useState } from "react";
import {
  HiMenu as MenuIcon,
  HiX as XIcon,
  HiUserCircle as UserIcon,
  HiCog as SettingsIcon,
  HiQuestionMarkCircle as SupportIcon,
  HiExternalLink as ExternalLinkIcon,
  HiLogout as LogoutIcon,
  HiSelector as SelectorIcon,
} from "react-icons/hi";
import { useIntercom } from "react-use-intercom";

import { useUser } from "@shared/auth";
import { Navbar, ProductSwitcher, SidebarOverlay } from "@shared/components";
import { urls } from "@shared/constants";
import { Avatar, Button, PicterLogo, Text } from "@shared/elements";

import { MESSAGE_PREFIX, paths } from "src/constants";

export default function AppHeader() {
  const t = useTranslations(`${MESSAGE_PREFIX}components.AppHeader`);
  const user = useUser();
  const { show: showIntercom } = useIntercom();
  const [showPicterAppsOverlay, setShowPicterAppsOverlay] = useState(false);

  return (
    <>
      <Disclosure as={Navbar.Container} fixed>
        {({ open }) => (
          <>
            <Navbar>
              <div className="flex">
                <div className="flex-shrink-0 flex items-center">
                  <Link href={urls.index}>
                    <PicterLogo
                      className="h-7 sm:h-8 w-auto"
                      logoClassName="md:hidden lg:flex"
                    />
                  </Link>
                </div>
                <div className="hidden md:ml-6 md:flex md:space-x-4">
                  <Navbar.Item href={paths.home}>
                    {t("opportunities")}
                  </Navbar.Item>
                  <Navbar.Item href={urls.mySubmissions}>
                    {t("mySubmissions")}
                  </Navbar.Item>
                </div>
              </div>
              <div className="hidden md:ml-6 md:flex md:items-center">
                {/* Profile dropdown */}
                <Menu as="div" className="ml-3 relative">
                  <div>
                    {!user.isLoading ? (
                      user.isAuthenticated ? (
                        <Menu.Button className="bg-white rounded-sm flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                          <span className="sr-only">{t("openUserMenu")}</span>
                          <Avatar size="sm" name={user.data.publicName} />
                          <div className="text-left ml-3">
                            <Text as="div" size="sm" className="-mb-1">
                              {user.data.publicName}
                            </Text>
                            <Text as="sub" size="sm" color="muted">
                              {user.data.email}
                            </Text>
                          </div>
                          <SelectorIcon className="w-5 h-5 ml-3 text-gray-400" />
                        </Menu.Button>
                      ) : (
                        <Link href={paths.login} passHref>
                          <Button variant="primary" size="sm" className="mr-2">
                            {t("login")}
                          </Button>
                        </Link>
                      )
                    ) : (
                      ""
                    )}
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-right absolute right-0 mt-2 w-52 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none divide-y divide-gray-100">
                      <div className="pb-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Navbar.MenuItem
                              href={urls.profile}
                              active={active}
                            >
                              <UserIcon
                                className="w-5 h-5 mr-3 text-gray-400"
                                aria-hidden="true"
                              />
                              {t("myProfile")}
                            </Navbar.MenuItem>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Navbar.MenuItem
                              href={urls.profile}
                              active={active}
                            >
                              <SettingsIcon
                                className="w-5 h-5 mr-3 text-gray-400"
                                aria-hidden="true"
                              />
                              {t("settings")}
                            </Navbar.MenuItem>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Navbar.MenuItem
                              onClick={showIntercom}
                              active={active}
                            >
                              <SupportIcon
                                className="w-5 h-5 mr-3 text-gray-400"
                                aria-hidden="true"
                              />
                              {t("support")}
                            </Navbar.MenuItem>
                          )}
                        </Menu.Item>
                      </div>
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Navbar.MenuItem
                              onClick={() => {
                                setShowPicterAppsOverlay(true);
                              }}
                              active={active}
                            >
                              <ExternalLinkIcon
                                className="w-5 h-5 mr-3 text-gray-400"
                                aria-hidden="true"
                              />
                              {t("moreApps")}
                            </Navbar.MenuItem>
                          )}
                        </Menu.Item>
                      </div>
                      <div className="pt-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Navbar.MenuItem
                              onClick={user.logout}
                              active={active}
                            >
                              <LogoutIcon
                                className="w-5 h-5 mr-3 text-gray-400"
                                aria-hidden="true"
                              />
                              {t("logout")}
                            </Navbar.MenuItem>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
              <div className="-mr-2 flex items-center md:hidden">
                {/* Mobile menu button: only display if user is logged in. Otherwise fallback to Login button */}
                {!user.isLoading ? (
                  user.isAuthenticated ? (
                    <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500">
                      <span className="sr-only">{t("openMainMenu")}</span>
                      {open ? (
                        <XIcon className="block h-6 w-6" aria-hidden="true" />
                      ) : (
                        <MenuIcon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      )}
                    </Disclosure.Button>
                  ) : (
                    <Link href={paths.login} passHref>
                      <Button variant="primary" size="sm" className="mr-2">
                        {t("login")}
                      </Button>
                    </Link>
                  )
                ) : (
                  ""
                )}
              </div>
            </Navbar>
            {/* Mobile menu: only visible for logged in users */}
            {!user.isLoading && user.isAuthenticated && (
              <Disclosure.Panel className="md:hidden">
                <div className="pt-2 pb-3 space-y-1">
                  <Disclosure.Button as={Navbar.ItemMobile} href={paths.home}>
                    {t("opportunities")}
                  </Disclosure.Button>
                  <Disclosure.Button
                    as={Navbar.ItemMobile}
                    href={urls.mySubmissions}
                  >
                    {t("mySubmissions")}
                  </Disclosure.Button>
                </div>
                <div className="pt-4 pb-3 border-t border-gray-200">
                  <div className="flex items-center px-4">
                    <Avatar
                      className="flex-shrink-0"
                      name={user.data.publicName}
                    />
                    <div className="ml-3">
                      <div className="text-base font-medium text-gray-800">
                        {user.data.publicName}
                      </div>
                      <div className="text-sm font-medium text-gray-500">
                        {user.data.email}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1 divide-y divide-gray-100">
                    <div className="pb-1">
                      <Disclosure.Button
                        as={Navbar.MenuItemMobile}
                        href={urls.profile}
                      >
                        <UserIcon
                          className="w-5 h-5 mr-3 text-gray-400"
                          aria-hidden="true"
                        />
                        {t("myProfile")}
                      </Disclosure.Button>
                      <Disclosure.Button
                        as={Navbar.MenuItemMobile}
                        href={urls.profile}
                      >
                        <SettingsIcon
                          className="w-5 h-5 mr-3 text-gray-400"
                          aria-hidden="true"
                        />
                        {t("settings")}
                      </Disclosure.Button>
                      <Disclosure.Button as="div">
                        <Navbar.MenuItemMobile onClick={showIntercom}>
                          <SupportIcon
                            className="w-5 h-5 mr-3 text-gray-400"
                            aria-hidden="true"
                          />
                          {t("support")}
                        </Navbar.MenuItemMobile>
                      </Disclosure.Button>
                    </div>
                    <div className="py-1">
                      <Disclosure.Button as="div">
                        <Navbar.MenuItemMobile
                          onClick={() => {
                            setShowPicterAppsOverlay(true);
                          }}
                        >
                          <ExternalLinkIcon
                            className="w-5 h-5 mr-3 text-gray-400"
                            aria-hidden="true"
                          />
                          {t("moreApps")}
                        </Navbar.MenuItemMobile>
                      </Disclosure.Button>
                    </div>
                    <div className="pt-1">
                      <Disclosure.Button as="div">
                        <Navbar.MenuItemMobile onClick={user.logout}>
                          <LogoutIcon
                            className="w-5 h-5 mr-3 text-gray-400"
                            aria-hidden="true"
                          />
                          {t("logout")}
                        </Navbar.MenuItemMobile>
                      </Disclosure.Button>
                    </div>
                  </div>
                </div>
              </Disclosure.Panel>
            )}
          </>
        )}
      </Disclosure>
      <SidebarOverlay
        show={showPicterAppsOverlay}
        size="narrow"
        onClose={() => {
          setShowPicterAppsOverlay(false);
        }}
      >
        <Fragment>
          <div className="px-4 sm:px-6 py-6 bg-gray-50">
            <div className="flex items-start justify-between">
              <Text as={SidebarOverlay.Title} size="lg">
                {t("moreAppsTitle")}
              </Text>
            </div>
          </div>
          <ProductSwitcher className="px-5 py-6 p-8" />
        </Fragment>
      </SidebarOverlay>
    </>
  );
}

AppHeader.messages = [
  `${MESSAGE_PREFIX}components.AppHeader`,
  ...SidebarOverlay.messages,
  ...ProductSwitcher.messages,
];
